import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type DotColor = "green" | "blue" | "red" | "amber" | "muted";

const DOT: Record<DotColor, string> = {
  green: "hsl(var(--dot-green))",
  blue: "hsl(var(--dot-blue))",
  red: "hsl(var(--dot-red))",
  amber: "hsl(var(--dot-amber))",
  muted: "hsl(var(--muted-foreground) / 0.5)",
};

interface DotChipProps {
  /** Omit for a hollow dot (draft, not yet matched). */
  color?: DotColor;
  /** Red text for states that need attention (overdue). */
  alert?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A status as a coloured dot plus a word — the transaction status chips on the
 * dashboard, invoice states, matched/unmatched receipts. One look for every
 * status in the app instead of pills, ticks and badges.
 */
export function DotChip({ color, alert, className, children }: DotChipProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium",
        alert ? "text-destructive" : "text-muted-foreground",
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "h-[7px] w-[7px] shrink-0 rounded-full",
          !color && "border-[1.5px] border-muted-foreground"
        )}
        style={color ? { backgroundColor: DOT[color] } : undefined}
      />
      {children}
    </span>
  );
}
